export type BillingPlan = "free" | "pro";

export interface BillingTier {
  id: BillingPlan;
  name: string;
  priceCents: number;
  priceLabel: string;
  periodLabel: string;
  description: string;
  features: string[];
  cta: string;
  highlighted?: boolean;
}

export interface BillingStatus {
  plan: BillingPlan;
  planName: string;
  isPaid: boolean;
  currentPeriodEnd: string | null;
  hasCustomerPortal: boolean;
}

export interface BillingUsageLimits {
  aiGenerationsPerDay: number | null;
  exportsPerMonth: number | null;
  savedDrafts: number | null;
}

export interface BillingUsage {
  aiGenerationsToday: number;
  exportsThisMonth: number;
  savedDraftsCount: number;
  limits: BillingUsageLimits;
  remainingAiGenerationsToday: number | null;
  remainingExportsThisMonth: number | null;
  remainingSavedDrafts: number | null;
}

export interface BillingOverview {
  billing: BillingStatus;
  usage: BillingUsage;
}

export const BILLING_TIERS: BillingTier[] = [
  {
    id: "free",
    name: "Free",
    priceCents: 0,
    priceLabel: "$0",
    periodLabel: "/mo",
    description: "Perfect for trying things out.",
    features: [
      "3 AI generations / day",
      "5 HTML exports / month",
      "10 saved drafts",
      "Browse all 148+ templates",
      "React export on paid plans",
      "Email support",
    ],
    cta: "Create free account",
  },
  {
    id: "pro",
    name: "Pro",
    priceCents: 1900,
    priceLabel: "$19",
    periodLabel: "/mo",
    description: "For individuals and small teams.",
    features: [
      "200 AI generations / day",
      "500 exports / month",
      "300 saved drafts",
      "Priority email support",
    ],
    cta: "Upgrade to Pro",
    highlighted: true,
  },
];

export const BILLING_TIER_BY_ID = Object.fromEntries(
  BILLING_TIERS.map((tier) => [tier.id, tier]),
) as Record<BillingPlan, BillingTier>;

/** Plans that can be purchased via Stripe checkout. */
export const PURCHASABLE_PLANS = BILLING_TIERS.filter(
  (tier) => tier.priceCents > 0,
);



export const BILLING_USAGE_LIMITS: Record<BillingPlan, BillingUsageLimits> = {
  free: {
    aiGenerationsPerDay: 3,
    exportsPerMonth: 5,
    savedDrafts: 10,
  },
  pro: {
    aiGenerationsPerDay: 200,
    exportsPerMonth: 500,
    savedDrafts: 300,
  },
};

export function isBillingPlan(value: string): value is BillingPlan {
  return value in BILLING_TIER_BY_ID;
}
